/**
 * RepBuddy - the hand-off into the verification page (SCRUM-49)
 *
 * Registration no longer ends in a session, so something has to carry two things across a
 * navigation: which address is being verified, and the password that goes with it. The
 * password is not just for signing in afterwards - the verification request itself carries
 * it, because redeeming the code is what sets the account's password (see the backend's
 * services/emailVerificationService.js for why registration alone cannot be trusted to).
 *
 * Two writers: the registration form, the moment the account is created, and the login
 * page, when a sign-in turns out to belong to an unverified account. Both hold the password
 * the user just typed, and both hand it over here so the user is not asked for it again.
 *
 * The two values have very different lifetimes, and this module exists to keep them apart.
 *
 *   email     sessionStorage. It survives a refresh on purpose - "which address is this
 *             code for" is the one thing the verification page cannot function without, and
 *             a user who hits F5 mid-signup should not be sent back to the form. It is the
 *             address they just typed into a public page; it is not a secret.
 *
 *   password  memory only, and deliberately nowhere else. It is a live credential, and the
 *             convenience of surviving a reload is not worth writing it into storage that
 *             any script on the origin can read and that outlives the tab's intent.
 *             Router state is not an option either: react-router keeps location.state in
 *             window.history, which IS persisted, so passing it through navigate() would
 *             quietly do the thing this comment is refusing to do.
 *
 * The cost of that choice is one honest fallback: refresh the verification page and the
 * password is gone, so the page asks for it alongside the code. One extra field on an
 * uncommon path, in exchange for never persisting it.
 */

const EMAIL_KEY = 'repbuddy_pending_verification_email';

// Module scope: cleared by a reload, a new tab, or clearPendingRegistration. That is the
// intent, not a limitation to work around.
let pendingPassword = null;
let pendingPasswordEmail = null;

function normalise(email) {
  return String(email || '').trim().toLowerCase();
}

/** Called when the user is sent to verification holding a password they just typed. */
export function rememberPendingRegistration({ email, password }) {
  const address = normalise(email);
  if (!address) return;

  pendingPassword = password || null;
  pendingPasswordEmail = address;

  try {
    sessionStorage.setItem(EMAIL_KEY, address);
  } catch {
    // Private mode, or storage disabled entirely. The page still works - it falls back to
    // the address passed in navigation state, and only a reload loses it.
  }
}

/** The address awaiting verification, or '' if we have none. */
export function getPendingEmail() {
  if (pendingPasswordEmail) return pendingPasswordEmail;
  try {
    return sessionStorage.getItem(EMAIL_KEY) || '';
  } catch {
    return '';
  }
}

/**
 * The password for `email`, if this tab is the one that was handed it. Returns null after a
 * reload, or when the address does not match the one we hold - a mismatch means the user
 * changed addresses mid-flow, and the password belongs to the other one.
 *
 * Reading does NOT clear it. Every verification attempt sends it, and a mistyped code
 * followed by a correct one is the ordinary case, not an edge. It is cleared once, when the
 * flow ends, by clearPendingRegistration.
 */
export function getPendingPassword(email) {
  const address = normalise(email);
  return pendingPassword && pendingPasswordEmail === address ? pendingPassword : null;
}

/** Called once verification is finished (either way) - nothing here outlives the flow. */
export function clearPendingRegistration() {
  pendingPassword = null;
  pendingPasswordEmail = null;
  try {
    sessionStorage.removeItem(EMAIL_KEY);
  } catch {
    // Nothing to clear if storage was never writable.
  }
}
